<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Location &amp; Device Tracking Log
      </oxd-text>
      <oxd-divider />

      <oxd-table-filter filter-title="Filter Records">
        <oxd-form @submit-valid="onSearch">
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.empNumber"
                  label="Employee #"
                  type="number"
                  placeholder="Leave blank for all"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.eventType"
                  label="Event Type"
                  type="select"
                  :options="eventTypeOptions"
                  :clear="true"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-actions>
            <oxd-button
              display-type="ghost"
              label="Reset"
              type="button"
              @click="onReset"
            />
            <oxd-button
              class="orangehrm-left-space"
              display-type="secondary"
              label="Search"
              type="submit"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>

      <oxd-divider />

      <div class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="items.data"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>

      <div
        v-if="!isLoading && items.data && items.data.length === 0"
        class="lt-empty"
      >
        <oxd-text tag="p" class="lt-empty-text">
          No tracking records found.
        </oxd-text>
      </div>

      <oxd-divider />

      <!-- OSM Map Section -->
      <div class="lt-map-section">
        <div class="lt-map-header">
          <oxd-text tag="p" class="lt-map-title">Location Map</oxd-text>
          <div class="lt-map-legend">
            <span class="lt-legend-item lt-legend-in">&#9679; Punch In</span>
            <span class="lt-legend-item lt-legend-out">&#9679; Punch Out</span>
            <span class="lt-legend-item lt-legend-track">&#9679; Tracking</span>
          </div>
        </div>
        <div v-if="mapLoading" class="lt-map-loading">
          <oxd-text tag="p">Loading map data&hellip;</oxd-text>
        </div>
        <div
          v-show="!mapLoading"
          ref="mapContainer"
          class="lt-map-container"
        ></div>
        <oxd-text
          v-if="!mapLoading && mapMarkerCount === 0"
          tag="p"
          class="lt-map-empty"
        >
          No records with location data to display.
        </oxd-text>
      </div>
    </div>
  </div>
</template>

<script>
import {reactive, ref} from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@/core/util/composable/usePaginate';

// Fix Leaflet default icon paths under webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const EVENT_TYPE_OPTIONS = [
  {id: null, label: 'All'},
  {id: 'punch_in', label: 'Punch In'},
  {id: 'punch_out', label: 'Punch Out'},
  {id: 'tracking', label: 'Tracking'},
];

const EVENT_TYPE_BADGE = {
  punch_in: {bg: '#e8f5e9', color: '#2e7d32'},
  punch_out: {bg: '#fff3e0', color: '#e65100'},
  tracking: {bg: '#e3f2fd', color: '#1565c0'},
};

function formatCoords(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return '—';
  }
  return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString();
}

function shortenDeviceId(id) {
  if (!id) return '—';
  if (id.length <= 20) return id;
  return id.slice(0, 8) + '…' + id.slice(-8);
}

const TrackingNormalizer = (data) => {
  return data.map((item) => {
    const badge = EVENT_TYPE_BADGE[item.eventType] ?? {
      bg: '#f5f5f5',
      color: '#616161',
    };
    return {
      id: item.id,
      empNumber: `#${item.empNumber}`,
      eventType: item.eventType.replace('_', ' '),
      eventBadgeStyle: `background:${badge.bg};color:${badge.color};padding:2px 8px;border-radius:10px;font-size:0.78rem;font-weight:600;white-space:nowrap;`,
      coords: formatCoords(item.latitude, item.longitude),
      deviceId: shortenDeviceId(item.deviceId),
      recordedAt: formatDate(item.recordedAt),
    };
  });
};

export default {
  name: 'LocationTrackingList',

  setup() {
    const filters = reactive({
      empNumber: null,
      eventType: null,
    });

    // empNumber=0 → API returns ALL records
    // empNumber=N → specific employee
    // no empNumber → self (we always pass 0 for admin view)
    const query = reactive({empNumber: 0});

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/location-tracking',
    );

    // Separate http instance for map (fetches up to 500 records without pagination)
    const mapHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/location-tracking',
    );

    const mapLoading = ref(false);
    const mapMarkerCount = ref(0);

    const {
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      showPaginator,
      execQuery,
    } = usePaginate(http, {
      query,
      normalizer: TrackingNormalizer,
      pageSize: 25,
    });

    return {
      filters,
      query,
      http,
      mapHttp,
      mapLoading,
      mapMarkerCount,
      total,
      pages,
      pageSize,
      currentPage,
      isLoading,
      showPaginator,
      items: response,
      execQuery,
    };
  },

  data() {
    return {
      eventTypeOptions: EVENT_TYPE_OPTIONS,
      headers: [
        {name: 'empNumber', title: 'Employee #', style: {flex: 1}},
        {
          name: 'eventType',
          title: 'Event',
          style: {flex: 1},
          cellRenderer: (index, row) => ({
            component: 'oxd-text',
            props: {tag: 'span', style: row.eventBadgeStyle},
            slotContent: row.eventType,
          }),
        },
        {name: 'coords', title: 'Location (Lat, Lng)', style: {flex: 2}},
        {name: 'deviceId', title: 'Device ID', style: {flex: 3}},
        {name: 'recordedAt', title: 'Recorded At', style: {flex: 2}},
      ],
      // Non-reactive Leaflet internals
      _map: null,
      _markerLayer: null,
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
      this.loadMapData();
    });
  },

  beforeUnmount() {
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
  },

  methods: {
    // ─────────────── Map ───────────────
    initMap() {
      const container = this.$refs.mapContainer;
      if (!container || this._map) return;

      this._map = L.map(container, {zoomControl: true}).setView(
        [30.3753, 69.3451], // default: Pakistan centre
        6,
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this._map);

      this._markerLayer = L.layerGroup().addTo(this._map);
    },

    async loadMapData() {
      if (!this._map) return;
      this.mapLoading = true;
      try {
        const empParam =
          this.query.empNumber !== undefined && this.query.empNumber !== null
            ? this.query.empNumber
            : 0;
        const response = await this.mapHttp.getAll({
          empNumber: empParam,
          limit: 500,
          offset: 0,
        });
        const records = (response.data?.data ?? []).filter(
          (r) =>
            r.latitude !== null &&
            r.latitude !== undefined &&
            r.longitude !== null &&
            r.longitude !== undefined,
        );
        this.renderMarkers(records);
      } catch {
        // silently ignore map errors; table still works
      } finally {
        this.mapLoading = false;
      }
    },

    renderMarkers(records) {
      if (!this._markerLayer) return;
      this._markerLayer.clearLayers();
      this.mapMarkerCount = records.length;

      const COLOR = {
        punch_in: '#2e7d32',
        punch_out: '#e65100',
        tracking: '#1565c0',
      };

      const bounds = [];
      records.forEach((r) => {
        const lat = parseFloat(r.latitude);
        const lng = parseFloat(r.longitude);
        if (isNaN(lat) || isNaN(lng)) return;

        const color = COLOR[r.eventType] ?? '#616161';
        const marker = L.circleMarker([lat, lng], {
          radius: 8,
          color: '#fff',
          weight: 1.5,
          fillColor: color,
          fillOpacity: 0.9,
        });

        const eventLabel = (r.eventType ?? '').replace('_', ' ');
        const recordedAt = r.recordedAt
          ? new Date(r.recordedAt).toLocaleString()
          : '—';
        marker.bindPopup(
          `<strong>${eventLabel.charAt(0).toUpperCase() + eventLabel.slice(1)}</strong><br>` +
            `Employee: #${r.empNumber}<br>` +
            `Lat: ${Number(lat).toFixed(5)}, Lng: ${Number(lng).toFixed(5)}<br>` +
            `<small>${recordedAt}</small>`,
        );

        this._markerLayer.addLayer(marker);
        bounds.push([lat, lng]);
      });

      if (bounds.length > 0) {
        this._map.fitBounds(bounds, {padding: [40, 40]});
      }
    },

    // ─────────────── Filter ───────────────
    onSearch() {
      const rawEmp = this.filters.empNumber;
      // Use 0 for "all records", specific number for an employee
      this.query.empNumber =
        rawEmp !== null && rawEmp !== '' && Number(rawEmp) > 0
          ? Number(rawEmp)
          : 0;
      this.execQuery();
      this.loadMapData();
    },

    onReset() {
      this.filters.empNumber = null;
      this.filters.eventType = null;
      this.query.empNumber = 0;
      this.execQuery();
      this.loadMapData();
    },
  },
};
</script>

<style scoped>
.lt-empty {
  text-align: center;
  padding: 24px 0;
}

.lt-empty-text {
  color: #999;
  font-size: 0.9rem;
}

.lt-map-section {
  margin-top: 8px;
}

.lt-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.lt-map-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
}

.lt-map-legend {
  display: flex;
  gap: 16px;
  font-size: 0.82rem;
  font-weight: 500;
}

.lt-legend-in {
  color: #2e7d32;
}

.lt-legend-out {
  color: #e65100;
}

.lt-legend-track {
  color: #1565c0;
}

.lt-map-container {
  width: 100%;
  height: 400px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.lt-map-loading,
.lt-map-empty {
  text-align: center;
  padding: 16px;
  color: #999;
  font-size: 0.9rem;
}
</style>
